/**
 * A wire field path, rendered as words.
 *
 * The server names a field the way the request body names it — `lines[0].quantityExpected`, `dockDoorId`,
 * `sealNumber` — and a validation summary that prints that verbatim asks the operator to read the API. A screen
 * that knows the field's own label should pass it; this is what happens when it does not, and it is still a long
 * way better than the raw path.
 *
 * Deliberately conservative: it splits and cases, it does not invent. `dockDoorId` becomes "Dock door" because a
 * trailing `Id` is an implementation detail of how the value travels, never part of what the operator chose.
 */
export function humanizeFieldPath(path: string | undefined): string | undefined {
  if (!path) return undefined;
  const trimmed = path.trim();
  if (!trimmed || trimmed === '_') return undefined;
  const segments = trimmed
    .split('.')
    .map((segment) => {
      const index = /^(.*?)\[(\d+)\]$/.exec(segment);
      const name = humanizeSegment(index ? index[1]! : segment);
      return index ? `${singular(name)} ${Number(index[2]) + 1}` : name;
    })
    .filter(Boolean);
  if (segments.length === 0) return undefined;
  const [head, ...rest] = segments;
  return [capitalize(head!), ...rest].join(' · ');
}

function humanizeSegment(segment: string): string {
  return segment
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\bId\b$/i, '')
    .trim()
    .toLocaleLowerCase();
}

/** `lines[0]` reads "Line 1", not "Lines 1". Only the regular plural, because guessing further would misname things. */
function singular(word: string): string {
  if (/(ss|us|is)$/i.test(word)) return word;
  if (/ies$/i.test(word)) return `${word.slice(0, -3)}y`;
  if (/s$/i.test(word)) return word.slice(0, -1);
  return word;
}

function capitalize(text: string): string {
  return text ? text.charAt(0).toLocaleUpperCase() + text.slice(1) : text;
}
